import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, ExternalLink, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import NewIdeaDialog from "@/components/ideas/NewIdeaDialog";
import JourneyStep from "@/components/home/JourneyStep";
import SignalChannels from "@/components/home/SignalChannels";

const VIDEO_URL = "https://www.tiktok.com/@lelav89_/video/7638629829339860254";
const VIDEO_ID = "7638629829339860254";
const CREATOR_HANDLE = "@lelav89_";
const CREATOR_URL = "https://www.tiktok.com/@lelav89_";

type Idea = { title: string; blurb: string; focusArea: string };
type Insight = {
  video_url: string;
  creator_handle: string;
  creator_url: string;
  summary: string;
  ideas: Idea[];
};

const TikTokIdeasSection = () => {
  const [prefill, setPrefill] = useState<{ title: string; description: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["tiktok-insight", VIDEO_URL],
    queryFn: async (): Promise<Insight> => {
      const { data: cached } = await supabase
        .from("tiktok_video_insights")
        .select("*")
        .eq("video_url", VIDEO_URL)
        .maybeSingle();
      if (cached) return cached as unknown as Insight;

      const { data: fresh, error: fnErr } = await supabase.functions.invoke("analyze-tiktok-video", {
        body: {
          video_url: VIDEO_URL,
          creator_handle: CREATOR_HANDLE,
          creator_url: CREATOR_URL,
        },
      });
      if (fnErr) throw fnErr;
      return fresh as Insight;
    },
    staleTime: 1000 * 60 * 60,
  });

  // Inject TikTok embed script once mounted
  useEffect(() => {
    const existing = document.querySelector<HTMLScriptElement>('script[src="https://www.tiktok.com/embed.js"]');
    if (existing) {
      // @ts-expect-error - tiktok injects this global
      window.tiktokEmbed?.lib?.render?.();
      return;
    }
    const s = document.createElement("script");
    s.src = "https://www.tiktok.com/embed.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  if (isLoading) {
    return (
      <section className="py-16 bg-background border-t border-border">
        <div className="container mx-auto px-6 flex items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Listening to community signals...
        </div>
      </section>
    );
  }

  if (error || !data?.ideas?.length) return null;

  return (
    <>
      <NewIdeaDialog
        open={!!prefill}
        onOpenChange={(o) => !o && setPrefill(null)}
        prefill={prefill ? { title: prefill.title, description: prefill.description, category: "Community" } : undefined}
      />
      <JourneyStep
        step="02"
        label="Strategic Communication"
        title={<>How we hear what's <span className="text-gradient-primary">moving</span></>}
        subhead="Our strategic communication practice begins by listening across social media and community channels — so client messaging meets people where the conversation already is. Today's example: a TikTok from Alabama."
      >
        <div className="grid lg:grid-cols-[380px_1fr] gap-10 items-start">
          <div>
            {/* Live Signal Embed */}
            <div className="rounded-[var(--radius)] border border-border bg-card/60 backdrop-blur-sm overflow-hidden shadow-card mb-4">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary animate-pulse-glow" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Live Signal · TikTok</span>
                </div>
                <a
                  href={CREATOR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 font-medium"
                >
                  {CREATOR_HANDLE} <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <div className="bg-background flex justify-center">
                <blockquote
                  className="tiktok-embed"
                  cite={VIDEO_URL}
                  data-video-id={VIDEO_ID}
                  style={{ maxWidth: "100%", minWidth: 280 }}
                >
                  <section>
                    <a target="_blank" rel="noreferrer" href={CREATOR_URL}>{CREATOR_HANDLE}</a>
                  </section>
                </blockquote>
              </div>
            </div>

            {/* AI Insight Panel */}
            <div className="rounded-[var(--radius)] border border-border bg-card/60 backdrop-blur-sm p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold text-foreground uppercase tracking-wider">About this video</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{data.summary}</p>
            </div>

            {/* Listening Channels */}
            <p className="text-[10px] font-semibold text-primary uppercase tracking-wider mb-2">
              Listening across
            </p>
            <SignalChannels size="sm" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                Trends we're tracking
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={() => setPrefill({ title: "", description: `Inspired by ${CREATOR_HANDLE} on TikTok: ${VIDEO_URL}` })}
              >
                Start your own idea <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
            {data.ideas.map((idea, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <button
                  onClick={() => setPrefill({
                    title: idea.title,
                    description: `${idea.blurb}\n\nInspired by ${CREATOR_HANDLE} on TikTok: ${VIDEO_URL}`,
                  })}
                  className="w-full text-left rounded-[var(--radius)] border border-border bg-card/60 hover:bg-card hover:border-primary/40 transition-all p-5 group"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-display text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                      {idea.title}
                    </h3>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0 mt-1" />
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                    {idea.blurb}
                  </p>
                  <span className="inline-block text-[10px] uppercase tracking-wider text-primary/80 font-medium bg-primary/10 px-2 py-1 rounded-full">
                    {idea.focusArea}
                  </span>
                </button>
              </motion.div>
            ))}
            </div>
          </div>
        </div>
      </JourneyStep>
    </>
  );
};

export default TikTokIdeasSection;
